import { ecsApi } from '@services';
import { eventEmitter } from '@utils';
import {
    GetNotificationsResponse,
    MarkAllSeenResponse,
    Notification,
    NotificationEntityType,
    NotificationType,
    RawNotification,
    RawNotificationsResponse
} from './notifications.types';

const mapEntity = (
    rawType: string,
    typeId: number
): { entityType?: NotificationEntityType; entityId?: number } => {
    if (rawType.startsWith('library.')) {
        const category = rawType.slice('library.'.length);
        switch (category) {
            case 'rh':
                return { entityType: 'bibliotheque-rh', entityId: typeId };
            case 'sst':
                return { entityType: 'bibliotheque-sst', entityId: typeId };
            case 'chantier':
                return { entityType: 'bibliotheque-chantier', entityId: typeId };
            default:
                return { entityType: 'document', entityId: typeId };
        }
    }

    const directMap: Record<string, NotificationEntityType> = {
        news: 'news',
        talk: 'causerie',
        formation: 'formation',
        conge: 'conge',
        pointage: 'pointage',
        quitus: 'quitus',
        testsheet: 'testsheet',
        'event-feedback': 'event-feedback'
    };

    const entityType = directMap[rawType];
    return entityType ? { entityType, entityId: typeId } : {};
};

const mapDisplayType = (rawType: string): NotificationType => {
    if (rawType === 'news') return 'news';
    if (rawType.startsWith('library.')) return 'document';
    return 'push';
};

const normalizeNotification = (raw: RawNotification): Notification => ({
    id: raw.id,
    title: raw.title,
    content: raw.content,
    date: raw.created_at,
    seen: raw.seen,
    type: mapDisplayType(raw.type),
    ...mapEntity(raw.type, raw.type_id)
});

export const notificationsApi = ecsApi.injectEndpoints({
    endpoints: (builder) => ({
        getNotifications: builder.mutation<GetNotificationsResponse, void>({
            query: () => ({
                url: '/notifications',
                method: 'GET'
            }),
            transformResponse: (raw: RawNotificationsResponse) => raw.map(normalizeNotification)
        }),
        markNotificationAsSeen: builder.mutation<{ message: string }, number>({
            query: (id) => ({
                url: `/notifications/${id}/seen`,
                method: 'PUT'
            }),
            onQueryStarted: async (_id, { queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    eventEmitter.emit('notifications:updated');
                } catch {
                    // No-op: errors surface via the base query error handling.
                }
            }
        }),
        markAllNotificationsAsSeen: builder.mutation<MarkAllSeenResponse, void>({
            query: () => ({
                url: '/notifications/seen-all',
                method: 'PUT'
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    eventEmitter.emit('notifications:updated');
                } catch {
                    // No-op: errors surface via the base query error handling.
                }
            }
        })
    })
});

export const {
    useGetNotificationsMutation,
    useMarkNotificationAsSeenMutation,
    useMarkAllNotificationsAsSeenMutation
} = notificationsApi;
